// Community-admin calls used by the onboarding wizard and the
// org-config Directory/Branding panels, backed by the generated Rust API client
// (@rubi/api-generated/clientRs). The client is configured in api/index.ts
// (baseURL, withCredentials, throwOnError), so these calls send the auth cookie,
// throw on non-2xx, and resolve to parsed `data`.
import {
  deleteOrgBranding,
  getCommunityPublic,
  getOrg,
  patchOrgAdmin,
  type PatchOrgAdminRequest,
  putOrgBranding,
} from "@rubi/api-generated/clientRs";

import { CommunityBranding } from "./communities";

// App namespaces an org can be granted (mirrors the app.* Permission values).
// Shared by the onboarding wizard and the org-directory admin panel.
export const APP_OPTIONS: { value: string; label: string }[] = [
  { value: "app.alert.", label: "Alerts" },
  { value: "app.report_v2.", label: "Online Reporting" },
  { value: "app.camera_registry.", label: "Camera Registry" },
  { value: "app.registry.", label: "Registry" },
  { value: "app.message.", label: "Messaging" },
  { value: "app.intel.", label: "Intel" },
  { value: "app.connect.", label: "Video Calls" },
  { value: "app.feedback.", label: "Feedback" },
];

export const SSO_PROVIDERS = ["Microsoft", "Okta", "Other"];

// Regional routing for an org. Drives which citizen host its services links use
// (ourcommunitysafety.ca vs .com) and which regional API the portal talks to.
export const API_REGIONS: { value: string; label: string }[] = [
  { value: "ca", label: "Canada (.ca)" },
  { value: "us", label: "United States (.com)" },
];

// Fields settable on rubi_org via PATCH /org/{id}/admin. Omit a field to leave
// it unchanged; send null to clear a nullable field.
export interface CommunityAdminPatch {
  short_title?: string | null;
  /** "ca" | "us". NOT NULL in the DB, so omit to leave unchanged — never null. */
  api_region?: string;
  province?: string | null;
  region_label?: string | null;
  sso_slug?: string | null;
  sso_provider?: string | null;
  services_url_slug?: string | null;
  display_on_services_map?: boolean;
  approved_apps?: string[];
  map_center?: { lat: number; lng: number } | null;
  /** Alert operational areas (division names). */
  operational_areas?: string[] | null;
}

// Current directory/discovery values for an org, read from GET /org/{id} (the
// authenticated rubi_org row). Used to pre-fill the directory editor for an
// existing org.
export interface CommunityAdminCurrent {
  title: string;
  short_title: string | null;
  api_region: string;
  province: string | null;
  region_label: string | null;
  sso_slug: string | null;
  sso_provider: string | null;
  services_url_slug: string | null;
  display_on_services_map: boolean;
  approved_apps: string[] | null;
  map_center: { lat: number; lng: number } | null;
  operational_areas: string[] | null;
}

export async function getCommunityAdmin(
  orgId: string,
): Promise<CommunityAdminCurrent> {
  const { data } = await getOrg({ path: { org_id: orgId } });
  const o = data!;
  const mc = o.map_center as unknown as { lat?: number; lng?: number } | null;
  const apps = o.approved_apps as unknown;
  return {
    title: o.title,
    short_title: o.short_title ?? null,
    api_region: o.api_region ?? "ca",
    province: o.province ?? null,
    region_label: o.region_label ?? null,
    sso_slug: o.sso_slug ?? null,
    sso_provider: o.sso_provider ?? null,
    services_url_slug: o.services_url_slug ?? null,
    display_on_services_map: !!o.display_on_services_map,
    approved_apps: Array.isArray(apps) ? (apps as string[]) : null,
    map_center:
      mc && typeof mc.lat === "number" && typeof mc.lng === "number"
        ? { lat: mc.lat, lng: mc.lng }
        : null,
    operational_areas: Array.isArray(o.operational_areas)
      ? (o.operational_areas as string[])
      : null,
  };
}

export async function patchCommunityAdmin(
  orgId: string,
  body: CommunityAdminPatch,
): Promise<void> {
  await patchOrgAdmin({
    path: { org_id: orgId },
    // CommunityAdminPatch is nearly a subset of the generated request (which also
    // carries loosely-typed JSON columns); the shapes match at runtime. NOTE:
    // api_region is accepted by the backend (api-rs patch_org_admin) but is not in
    // the committed generated type yet — the client was last generated before that
    // field existed. This cast is what lets it through; drop the note once
    // `yarn openapi-codegen` has run against a backend carrying it.
    body: body as unknown as PatchOrgAdminRequest,
  });
}

// Branding kinds offered in the admin UI (the full backend BrandingKind set).
export const BRANDING_KINDS = [
  "crest",
  "login_logo",
  "login_background",
  "avatar",
  "hero",
  "crest_wordmark",
] as const;
export type BrandingKind = (typeof BRANDING_KINDS)[number];

// hero & login_background may be a video; the others are image-only (mirrors the
// backend BrandingKind::allows_video).
export const VIDEO_CAPABLE_KINDS: ReadonlySet<BrandingKind> = new Set([
  "hero",
  "login_background",
]);

// Human labels for each branding kind. Shared by the wizard and the branding
// admin panel.
export const BRANDING_LABELS: Record<BrandingKind, string> = {
  crest: "Crest / logo",
  login_logo: "Login logo",
  login_background: "Login background (image or video)",
  avatar: "Avatar",
  hero: "Citizen hero (image or video)",
  crest_wordmark: "Crest + wordmark",
};

// Reads an org's current branding assets from the public community projection
// (GET /public/communities/{id}). Returns an empty object when the org has no
// public projection (e.g. a Rubicon-admin org, which the endpoint 404s) — the
// caller can still upload.
export async function getCommunityBranding(
  orgId: string,
): Promise<CommunityBranding> {
  try {
    const { data } = await getCommunityPublic({
      path: { id_or_slug: orgId },
    });
    return data?.branding ?? {};
  } catch (e) {
    // Admin orgs (and unknown ids) 404 — no branding to show; the caller can
    // still upload. Any other error propagates.
    const status =
      (e as { status?: number; response?: { status?: number } })?.status ??
      (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return {};
    throw e;
  }
}

// Clears the branding kind on the org row and best-effort deletes the hosted S3
// object. Returns the URL that was cleared (null if the kind wasn't set).
export async function deleteCommunityBranding(
  orgId: string,
  kind: BrandingKind,
): Promise<string | null> {
  const { data } = await deleteOrgBranding({
    path: { org_id: orgId, kind },
  });
  return data?.cleared_url ?? null;
}

export async function uploadCommunityBranding(
  orgId: string,
  kind: BrandingKind,
  file: File,
): Promise<{ url: string; is_video?: boolean }> {
  const { data } = await putOrgBranding({
    path: { org_id: orgId, kind },
    // putOrgBranding sets bodySerializer: null, so the File is sent as raw bytes
    // (not JSON). Override the generated octet-stream default with the real MIME
    // type — the backend derives the image/video format from Content-Type.
    body: file as unknown as string,
    headers: { "Content-Type": file.type },
  });
  return data as { url: string; is_video?: boolean };
}
